import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LogIn, Mail } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill } from "@/components/dryvn";
import { firebaseAuth, signInEmail, signInGoogle, subscribeAuth } from "@/lib/firebase";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — Dryvn Life" },
      { name: "description", content: "Sign in to Dryvn Life." },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const configured = !!firebaseAuth();

  useEffect(() => {
    return subscribeAuth((u) => {
      if (u) navigate({ to: "/" });
    });
  }, [navigate]);

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInEmail(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <HeroCard
        eyebrow="Welcome back"
        title={
          <>
            Sign in to{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              Dryvn Life
            </em>
            .
          </>
        }
      >
        <p className="max-w-md text-sm" style={{ color: "var(--dryvn-grey)" }}>
          Your morning briefing, your household, your quiet dashboard — right where you left it.
        </p>
      </HeroCard>

      <DCard className="mx-auto mt-8 max-w-md">
        {!configured && (
          <div
            className="mb-4 rounded-xl p-3 text-xs"
            style={{ backgroundColor: "var(--dryvn-page-tint)", color: "var(--dryvn-grey)" }}
          >
            Firebase not configured. Set <code>VITE_FIREBASE_*</code> environment variables to
            enable sign-in.
          </div>
        )}

        <form onSubmit={handleEmail} className="space-y-3">
          <label className="block text-xs" style={{ color: "var(--dryvn-grey)" }}>
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
              style={{
                borderColor: "var(--dryvn-line)",
                backgroundColor: "var(--dryvn-card)",
                color: "var(--dryvn-ink)",
              }}
            />
          </label>
          <label className="block text-xs" style={{ color: "var(--dryvn-grey)" }}>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
              style={{
                borderColor: "var(--dryvn-line)",
                backgroundColor: "var(--dryvn-card)",
                color: "var(--dryvn-ink)",
              }}
            />
          </label>

          {error && (
            <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !configured}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "var(--dryvn-primary)" }}
          >
            <Mail className="h-4 w-4" /> Sign in with email
          </button>
        </form>

        <div
          className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider"
          style={{ color: "var(--dryvn-grey)" }}
        >
          <span className="h-px flex-1" style={{ backgroundColor: "var(--dryvn-line)" }} /> or{" "}
          <span className="h-px flex-1" style={{ backgroundColor: "var(--dryvn-line)" }} />
        </div>

        <button
          onClick={handleGoogle}
          disabled={busy || !configured}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
          style={{
            borderColor: "var(--dryvn-line)",
            color: "var(--dryvn-ink)",
            backgroundColor: "var(--dryvn-card)",
          }}
        >
          <LogIn className="h-4 w-4" /> Continue with Google
        </button>

        <p className="mt-4 text-center text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
          <Link to="/">Return home</Link> <span className="mx-1 opacity-40">·</span>
          <Pill tone="muted">Firebase Auth</Pill>
        </p>
      </DCard>
    </AppShell>
  );
}
